import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { CartContext, type CartContextValue, type ResolvedCartLine } from "../utils/cartContext";
import { readCart, writeCart, type CartLineItem } from "../utils/cartStore";
import { seafoodItems } from "../data/seafood";
import { cutOf, prepFee, type CutId } from "../data/cuts";
import { packById, packPrice, type PackId } from "../data/packs";

const itemsById = new Map(seafoodItems.map((item) => [item.id, item]));

export default function CartProvider({ children }: { children: ReactNode }) {
  // Lazy initializer reads localStorage synchronously before first paint — same as CookieConsentProvider.
  const [cart, setCart] = useState<CartLineItem[]>(() => readCart());

  useEffect(() => {
    writeCart(cart);
  }, [cart]);

  const setQty = useCallback((id: string, pack: PackId, qty: number, cut?: CutId) => {
    setCart((prev) => {
      const rest = prev.filter((line) => !(line.id === id && line.pack === pack));
      if (qty <= 0) return rest;
      const at = prev.findIndex((line) => line.id === id && line.pack === pack);
      // One cut per catch: an explicit cut wins, else keep what this catch already has, else the default ("").
      const chosen = cut ?? prev.find((line) => line.id === id)?.cut ?? "";
      const next = { id, pack, qty: Math.floor(qty), cut: chosen };
      // Keep the line where it was, so editing a quantity doesn't reshuffle the cart.
      return at === -1 ? [...rest, next] : [...rest.slice(0, at), next, ...rest.slice(at)];
    });
  }, []);

  const removeItem = useCallback((id: string, pack: PackId) => {
    setCart((prev) => prev.filter((line) => !(line.id === id && line.pack === pack)));
  }, []);

  const clear = useCallback(() => setCart([]), []);

  const lines: ResolvedCartLine[] = useMemo(
    () =>
      cart
        .map((line) => {
          const item = itemsById.get(line.id);
          if (!item) return null;
          const pack = packById(line.pack);
          const cut = cutOf(item, line.cut);
          const unitPrice = packPrice(item, pack) + prepFee(cut, pack);
          return { item, pack, cut, qty: line.qty, unitPrice, lineTotal: unitPrice * line.qty };
        })
        .filter((line): line is ResolvedCartLine => line !== null),
    [cart]
  );

  const itemCount = useMemo(() => lines.reduce((sum, line) => sum + line.qty, 0), [lines]);
  const totalKg = useMemo(() => lines.reduce((sum, line) => sum + line.qty * line.pack.kg, 0), [lines]);
  const subtotal = useMemo(() => lines.reduce((sum, line) => sum + line.lineTotal, 0), [lines]);
  const cartIds = useMemo(() => new Set(cart.map((line) => line.id)), [cart]);

  const value: CartContextValue = {
    lines,
    itemCount,
    totalKg,
    subtotal,
    isInCart: (id: string) => cartIds.has(id),
    qtyOf: (id: string, pack: PackId) => cart.find((line) => line.id === id && line.pack === pack)?.qty ?? 0,
    setQty,
    cutIdOf: (id: string) => cart.find((line) => line.id === id)?.cut,
    removeItem,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
